<script setup lang="ts">
    import BackButton from '@/components/BackButton.ce.vue';
    import { type TutorialStepTyping } from '@/typings/widget';
    import TutorialCard from './TutorialCard.ce.vue';

    defineProps<{
        inPath?: boolean
    }>();

    const emit = defineEmits<{(event: 'next' | 'backToPath')}>();

    const { t } = useI18n();
    const { isTablet } = useAppBreakpoints();

    // Refs
    const activeStep = ref(1);

    // Computed
    const steps: ComputedRef<TutorialStepTyping[]> = computed(() => [
        {
            index: 1,
            title: t('widget.tutorial.step-1.title'),
            description: t('widget.tutorial.step-1.desc'),
            image: `${import.meta.env.VITE_CDN_URL}/static/illu_step1.svg`,
            imageMobile: `${import.meta.env.VITE_CDN_URL}/static/illu_step1_mob.svg`,
            button: t('widget.tutorial.step-1.button'),
        },
        {
            index: 2,
            title: t('widget.tutorial.step-2.title'),
            description: t('widget.tutorial.step-2.desc'),
            image: `${import.meta.env.VITE_CDN_URL}/static/illu_step2.svg`,
            imageMobile: `${import.meta.env.VITE_CDN_URL}/static/illu_step2_mob.svg`,
            button: t('widget.tutorial.step-2.button'),
        },
        {
            index: 3,
            title: t('widget.tutorial.step-3.title'),
            description: t('widget.tutorial.step-3.desc'),
            image: `${import.meta.env.VITE_CDN_URL}/static/illu_step3.svg`,
            imageMobile: `${import.meta.env.VITE_CDN_URL}/static/illu_step3_mob.svg`,
            button: t('widget.tutorial.step-3.button'),
        },
    ]);

    // Methods
    const handleNextStep = (): void => {
        if (activeStep.value === steps.value.length) {
            emit('next');
            return;
        }
        activeStep.value = activeStep.value + 1;
    };

    onBeforeUnmount(() => {
        activeStep.value = 1;
    });
</script>

<template>
    <div class="tutorial">
        <div
            v-if="inPath"
            class="tutorial__back-btn"
        >
            <BackButton :on-click="() => emit('backToPath')" />
        </div>
        <template
            v-for="step in steps"
            :key="step.index"
        >
            <TutorialCard
                v-if="activeStep === step.index"
                :step="step"
                :total="steps.length"
                @next="handleNextStep"
            />
        </template>
        <button
            v-if="activeStep !== steps.length"
            type="button"
            :class="{'btn--thin': !isTablet}"
            class="btn btn--secondary tutorial__skip"
            @click="emit('next')"
        >
            <span>
                {{ t('widget.tutorial.skip') }}
            </span>
        </button>
    </div>
</template>

<style lang="scss" scoped>
.tutorial {
    position: relative;
    background: var(--background-primary);
    block-size: 100%;
    inline-size: 100%;

    &__skip {
        position: absolute;
        inset-block: var(--spacing-04) auto;
        inset-inline-end: var(--spacing-03);

        @include min-width(lg) {
            inset-block: auto var(--spacing-06);
            inset-inline-end: var(--spacing-05);
        }
    }

    &__back-btn {
        position: absolute;
        z-index: var(--zindex-stack-thrice);
        inset-block-start: 0;
        inset-inline-start: 0;
        line-height: 1;
        margin-block: var(--spacing-04);
        margin-inline: 0;
        padding-block: 0;
        padding-inline: var(--widget-inner-gutter);
    }
}
</style>
